import { useState, useEffect } from "react";
import AudioPlayer from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";
import { FaSpotify, FaApple, FaAmazon, FaYoutube } from "react-icons/fa";
import { getAllSongs } from "../firebase/firebase-functions";
import "./musicplayer.css";

const MusicPlayer = () => {
  const [currentTrack, setCurrentTrack] = useState(0);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Fetch songs from Firestore on component mount
  useEffect(() => {
    fetchSongs();
  }, []);

  const fetchSongs = async () => {
    setLoading(true);
    setError("");

    try {
      const result = await getAllSongs();

      if (result.success && result.songs.length > 0) {
        // Transform Firestore data to match the tracks format
        const formattedTracks = result.songs.map((song) => ({
          id: song.id,
          title: song.title,
          artist: song.artist,
          src: song.audioUrl,
          cover: song.coverUrl,
          isLatest: song.isLatest || false,
          streamingLinks: song.streamingLinks || {
            spotify: "",
            apple: "",
            amazon: "",
            youtube: "",
          },
        }));

        setTracks(formattedTracks);
      } else if (result.songs.length === 0) {
        setError("No songs available yet. Check back soon!");
      } else {
        setError(result.error || "Failed to load songs");
      }
    } catch (err) {
      setError("Failed to load songs. Please try again later.");
      console.error("Error fetching songs:", err);
    } finally {
      setLoading(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="music-player-container">
        <div className="player-loading">
          <div className="loader-music"></div>
          <p>Loading music...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || tracks.length === 0) {
    return (
      <div className="music-player-container">
        <div className="player-error">
          <p>{error || "No songs available"}</p>
        </div>
      </div>
    );
  }

  const currentSong = tracks[currentTrack];

  const handleNext = () => {
    setCurrentTrack((prev) => (prev + 1) % tracks.length);
  };

  const handlePrevious = () => {
    setCurrentTrack((prev) => (prev - 1 + tracks.length) % tracks.length);
  };

  return (
    <div className="music-player-container">
      <div className="track-info">
        <div className="album-art">
          <img src={currentSong.cover} alt={`${currentSong.title} cover`} />
          {currentSong.isLatest && (
            <div className="latest-badge">Latest Release</div>
          )}
        </div>

        <div className="track-details">
          <h3 className="track-title">{currentSong.title}</h3>
          <p className="track-artist">{currentSong.artist}</p>

          {/* Track List */}
          <div className="track-list">
            {tracks.map((track, index) => (
              <div
                key={track.id}
                className={`track-item ${
                  index === currentTrack ? "active" : ""
                }`}
                onClick={() => setCurrentTrack(index)}
              >
                <span className="track-number">{index + 1}</span>
                <span className="track-name">{track.title}</span>
                {track.isLatest && (
                  <span className="latest-indicator">NEW</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="audio-player-wrapper">
        <AudioPlayer
          src={currentSong.src}
          onClickNext={handleNext}
          onClickPrevious={handlePrevious}
          showSkipControls={tracks.length > 1}
          showJumpControls={false}
          customAdditionalControls={[]}
          customVolumeControls={[]}
          layout="horizontal-reverse"
          className="custom-audio-player"
        />
      </div>

      {/* Dynamic Streaming Links based on current track */}
      <div className="streaming-section">
        <h4 className="streaming-title">Listen to {currentSong.title} on:</h4>
        <div className="streaming-links">
          {currentSong.streamingLinks.spotify && (
            <a
              href={currentSong.streamingLinks.spotify}
              target="_blank"
              rel="noopener noreferrer"
              className="streaming-link spotify"
              aria-label={`Listen to ${currentSong.title} on Spotify`}
            >
              <FaSpotify />
              <span>Spotify</span>
            </a>
          )}
          {currentSong.streamingLinks.apple && (
            <a
              href={currentSong.streamingLinks.apple}
              target="_blank"
              rel="noopener noreferrer"
              className="streaming-link apple"
              aria-label={`Listen to ${currentSong.title} on Apple Music`}
            >
              <FaApple />
              <span>Apple Music</span>
            </a>
          )}
          {currentSong.streamingLinks.amazon && (
            <a
              href={currentSong.streamingLinks.amazon}
              target="_blank"
              rel="noopener noreferrer"
              className="streaming-link amazon"
              aria-label={`Listen to ${currentSong.title} on Amazon Music`}
            >
              <FaAmazon />
              <span>Amazon Music</span>
            </a>
          )}
          {currentSong.streamingLinks.youtube && (
            <a
              href={currentSong.streamingLinks.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="streaming-link youtube"
              aria-label={`Watch ${currentSong.title} on YouTube`}
            >
              <FaYoutube />
              <span>YouTube</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default MusicPlayer;
